import type { Note, NoteStats } from '../types/music';
import { calculateNoteWeight } from './adaptiveLogic';

/**
 * Maximum number of recent notes to track for avoiding repetition
 */
const MAX_RECENT_NOTES = 5;

/**
 * Select a note from the deck using weighted random selection
 *
 * Notes with lower accuracy and slower response times are more likely to be selected.
 * Recently shown notes are penalized to avoid repetition.
 */
export function selectWeightedNote(
  deck: Note[],
  stats: Map<string, NoteStats>,
  recentNotes: string[]
): Note | null {
  if (deck.length === 0) return null;
  if (deck.length === 1) return deck[0];

  // Calculate weights for all notes
  const weights: { note: Note; weight: number }[] = deck.map(note => ({
    note,
    weight: calculateNoteWeight(note.id, stats, recentNotes)
  }));

  // Calculate total weight
  const totalWeight = weights.reduce((sum, w) => sum + w.weight, 0);

  // Random selection based on weights
  let random = Math.random() * totalWeight;

  for (const { note, weight } of weights) {
    random -= weight;
    if (random <= 0) {
      return note;
    }
  }

  // Fallback to last note (should rarely happen due to floating point)
  return weights[weights.length - 1].note;
}

/**
 * Update the recent notes list
 */
export function updateRecentNotes(recentNotes: string[], noteId: string): string[] {
  // Remove if already exists
  const filtered = recentNotes.filter(id => id !== noteId);
  // Add to front
  filtered.unshift(noteId);
  // Keep only the most recent
  return filtered.slice(0, MAX_RECENT_NOTES);
}

/**
 * Get a simple random note (no weighting)
 */
export function selectRandomNote(deck: Note[]): Note | null {
  if (deck.length === 0) return null;
  const index = Math.floor(Math.random() * deck.length);
  return deck[index];
}

/**
 * Shuffle an array (Fisher-Yates algorithm)
 */
export function shuffleArray<T>(array: T[]): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/**
 * Get the next note for the practice session
 *
 * This is the main entry point for note selection.
 * It handles both weighted and random selection based on the useWeighted flag.
 */
export function getNextNote(
  deck: Note[],
  stats: Map<string, NoteStats>,
  recentNotes: string[],
  useWeighted: boolean = true
): Note | null {
  if (useWeighted) {
    return selectWeightedNote(deck, stats, recentNotes);
  }
  return selectRandomNote(deck);
}
